/* CX · interacciones de las secciones estilo Concept. Sin dependencias. */
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* Animación de entrada */
const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => { for (const entry of entries) { if (entry.isIntersecting) { entry.target.classList.add('cx-in'); revealObserver.unobserve(entry.target); } } }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }) : null;
function observeReveals(root = document) {
  root.querySelectorAll('.cx-section, .cx-reveal-root').forEach((el) => { if (!revealObserver || reduceMotion) el.classList.add('cx-in'); else revealObserver.observe(el); });
}

/* Slideshow del hero */
class CxSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.cx-hero__slide')];
    this.dots = [...this.querySelectorAll('.cx-hero__dot')];
    this.counter = this.querySelector('[data-cx-count]');
    this.speed = Number(this.dataset.speed || 6) * 1000;
    this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1 && !reduceMotion;
    this.index = 0;
    this.style.setProperty('--cx-speed', (this.speed / 1000) + 's');
    this.querySelector('[data-cx-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-cx-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.play());
    this.addEventListener('focusin', () => this.pause());
    let startX = null;
    this.addEventListener('pointerdown', (e) => { startX = e.clientX; }, { passive: true });
    this.addEventListener('pointerup', (e) => { if (startX === null) return; const dx = e.clientX - startX; if (Math.abs(dx) > 50) this.go(this.index + (dx < 0 ? 1 : -1)); startX = null; });
    this.addEventListener('shopify:block:select', (e) => { const i = this.slides.indexOf(e.target.closest('.cx-hero__slide')); if (i > -1) { this.go(i); this.pause(); } });
    this.go(0, true);
  }
  disconnectedCallback() { clearTimeout(this.timer); }
  go(i, initial = false) {
    const n = this.slides.length;
    if (!n) return;
    this.index = (i + n) % n;
    this.slides.forEach((s, k) => {
      const on = k === this.index;
      s.classList.toggle('is-active', on);
      s.setAttribute('aria-hidden', String(!on));
      s.querySelectorAll('a, button').forEach((el) => (on ? el.removeAttribute('tabindex') : el.setAttribute('tabindex', '-1')));
      const video = s.querySelector('video');
      if (video) { if (on) { const p = video.play?.(); if (p && p.catch) p.catch(() => {}); } else { video.pause?.(); } }
    });
    this.dots.forEach((d, k) => { d.classList.remove('is-active'); d.setAttribute('aria-current', String(k === this.index)); });
    void this.offsetWidth;
    this.dots[this.index]?.classList.add('is-active');
    if (this.counter) this.counter.textContent = String(this.index + 1).padStart(2, '0') + ' / ' + String(n).padStart(2, '0');
    if (!initial || this.autoplay) this.schedule();
  }
  schedule() { clearTimeout(this.timer); if (!this.autoplay || this.paused) return; this.timer = setTimeout(() => this.go(this.index + 1), this.speed); }
  pause() { this.paused = true; this.classList.add('is-paused'); clearTimeout(this.timer); }
  play() { this.paused = false; this.classList.remove('is-paused'); this.schedule(); }
}

/* Carrusel con flechas */
class CxCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.cx-track');
    this.prev = this.querySelector('[data-cx-prev]');
    this.next = this.querySelector('[data-cx-next]');
    if (!this.track) return;
    const step = () => this.track.clientWidth * 0.8;
    this.prev?.addEventListener('click', () => this.track.scrollBy({ left: -step() }));
    this.next?.addEventListener('click', () => this.track.scrollBy({ left: step() }));
    this.track.addEventListener('scroll', () => this.update(), { passive: true });
    window.addEventListener('resize', () => this.update(), { passive: true });
    this.update();
  }
  update() { const t = this.track; if (this.prev) this.prev.disabled = t.scrollLeft < 4; if (this.next) this.next.disabled = t.scrollLeft + t.clientWidth >= t.scrollWidth - 4; }
}

/* Coche con puntos */
class CxHotspots extends HTMLElement {
  connectedCallback() {
    this.spots = [...this.querySelectorAll('.cx-spot')];
    this.items = [...this.querySelectorAll('.cx-spots__item')];
    this.spots.forEach((spot, i) => { spot.querySelector('.cx-spot__btn')?.addEventListener('click', (e) => { e.stopPropagation(); this.open(spot.classList.contains('is-open') ? -1 : i); }); });
    this.items.forEach((item, i) => { item.addEventListener('mouseenter', () => this.open(i)); item.addEventListener('focus', () => this.open(i)); });
    document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.open(-1); });
    this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.open(-1); });
    this.addEventListener('shopify:block:select', (e) => { const i = this.spots.indexOf(e.target.closest('.cx-spot')); if (i > -1) this.open(i); });
  }
  open(index) {
    this.spots.forEach((s, i) => { s.classList.toggle('is-open', i === index); s.querySelector('.cx-spot__btn')?.setAttribute('aria-expanded', String(i === index)); });
    this.items.forEach((it, i) => it.classList.toggle('is-active', i === index));
  }
}

/* Pestañas */
class CxTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (e) => { if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length; this.select(n); this.tabs[n].focus(); } });
    });
    this.addEventListener('shopify:block:select', (e) => { const i = this.panels.indexOf(e.target.closest('[role="tabpanel"]')); if (i > -1) this.select(i); });
  }
  select(index) {
    this.tabs.forEach((t, i) => { t.setAttribute('aria-selected', String(i === index)); t.tabIndex = i === index ? 0 : -1; });
    this.panels.forEach((p, i) => { p.hidden = i !== index; });
  }
}

/* Parallax suave en banners */
function initParallax() {
  if (reduceMotion) return;
  const els = [...document.querySelectorAll('[data-cx-parallax]')];
  if (!els.length) return;
  let ticking = false;
  const update = () => {
    const vh = window.innerHeight;
    els.forEach((el) => { const r = el.parentElement.getBoundingClientRect(); if (r.bottom < 0 || r.top > vh) return; const p = (r.top + r.height / 2 - vh / 2) / vh; el.style.transform = 'translate3d(0, ' + (-p * 10).toFixed(2) + '%, 0)'; });
    ticking = false;
  };
  window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
  update();
}

/* Configuración compartida (la escribe la sección Carrito y buscador) */
const getTheme = () => (typeof Theme !== 'undefined' ? Theme : window.Theme);
function getConfig() {
  if (window.__cxConfig) return window.__cxConfig;
  let cfg = {};
  try { cfg = JSON.parse(document.querySelector('[data-cx-config]')?.textContent || '{}'); } catch (e) { cfg = {}; }
  const theme = getTheme();
  cfg.routes = Object.assign({ cart: theme?.routes?.cart_url || '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', search: '/search', predictive: '/search/suggest', root: '/' }, cfg.routes || {});
  cfg.strings = cfg.strings || {};
  cfg.moneyFormat = cfg.moneyFormat || '{{amount_with_comma_separator}} €';
  window.__cxConfig = cfg;
  return cfg;
}
const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;' };
ESC[String.fromCharCode(34)] = '&quot;';
ESC[String.fromCharCode(39)] = '&#39;';
const escapeHtml = (v) => { const s = String(v ?? ''); let out = ''; for (const ch of s) out += ESC[ch] || ch; return out; };
function groupDigits(intStr, sep) { let out = ''; for (let k = 0; k < intStr.length; k++) { if (k > 0 && (intStr.length - k) % 3 === 0) out += sep; out += intStr[k]; } return out; }
function formatMoney(cents, format = getConfig().moneyFormat) {
  const value = Number(cents || 0) / 100;
  const fmt = (n, dec, thousands, decimal) => { const parts = n.toFixed(dec).split('.'); return groupDigits(parts[0], thousands) + (parts[1] ? decimal + parts[1] : ''); };
  const f = String(format);
  const a = f.indexOf('{{'); const b = f.indexOf('}}', a);
  if (a < 0 || b < 0) return fmt(value, 2, '.', ',');
  const key = f.slice(a + 2, b).trim();
  let val;
  switch (key) {
    case 'amount': val = fmt(value, 2, ',', '.'); break;
    case 'amount_no_decimals': val = fmt(value, 0, ',', '.'); break;
    case 'amount_with_comma_separator': val = fmt(value, 2, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator': val = fmt(value, 0, '.', ','); break;
    case 'amount_with_apostrophe_separator': val = fmt(value, 2, String.fromCharCode(39), '.'); break;
    case 'amount_with_space_separator': val = fmt(value, 2, ' ', ','); break;
    default: val = fmt(value, 2, '.', ',');
  }
  return f.slice(0, a) + val + f.slice(b + 2);
}
const sizedImage = (url, width = 240) => { if (!url) return ''; if (url.startsWith('data:')) return url; const u = url.startsWith('//') ? 'https:' + url : url; return u + (u.includes('?') ? '&' : '?') + 'width=' + width; };
const ICON_ARROW = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';
function updateCartCounts(count) { document.querySelectorAll('[data-cx-cart-count]').forEach((el) => { el.textContent = count; el.hidden = !count; }); }

/* Añadir al carrito desde las tarjetas */
async function addToCart(form) {
  const cfg = getConfig();
  const button = form.querySelector('button[type="submit"]');
  const original = button?.innerHTML;
  button?.classList.add('is-loading');
  let events = null;
  try { events = await import('@shopify/events'); } catch (e) { events = null; }
  const formData = new FormData(form);
  let deferred = null;
  if (events?.CartLinesUpdateEvent) {
    deferred = events.CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(new events.CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(formData.get('id')), quantity: Number(formData.get('quantity') || 1) }], promise: deferred.promise }));
  }
  try {
    const res = await fetch(cfg.routes.cartAdd + '.js', { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData }).then((r) => r.json());
    const cart = await fetch(cfg.routes.cart + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const didError = Boolean(res.status);
    if (deferred) deferred.resolve({ cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'cx-product-card', itemCount: 1, didError } });
    button?.classList.remove('is-loading');
    if (didError) { if (button) { button.innerHTML = escapeHtml(res.description || res.message || original); setTimeout(() => { button.innerHTML = original; }, 2500); } return; }
    if (button) { button.classList.add('is-added'); button.innerHTML = escapeHtml(button.dataset.addedText || '✓'); setTimeout(() => { button.classList.remove('is-added'); button.innerHTML = original; }, 1800); }
    updateCartCounts(cart.item_count);
    document.dispatchEvent(new CustomEvent('cx:cart:changed', { detail: { cart, open: true, handled: Boolean(deferred) } }));
    if (!document.querySelector('cx-cart') && !document.querySelector('cart-drawer-component')) window.location.href = cfg.routes.cart;
  } catch (err) {
    deferred?.reject?.(err);
    form.submit();
  }
}
document.addEventListener('submit', (e) => { const form = e.target; if (!(form instanceof HTMLFormElement) || !form.matches('[data-cx-add]')) return; e.preventDefault(); addToCart(form); });

/* Cajones (carrito, buscador, menú móvil) */
const Drawers = {
  current: null,
  lastFocus: null,
  get(name) { return { drawer: document.querySelector('[data-cx-drawer="' + name + '"]'), overlay: document.querySelector('[data-cx-overlay="' + name + '"]') }; },
  open(name, trigger) {
    const { drawer, overlay } = this.get(name);
    if (!drawer) return false;
    if (this.current && this.current !== name) this.close(true);
    Header.closeAll();
    this.lastFocus = trigger || document.activeElement;
    drawer.classList.add('is-open');
    overlay?.classList.add('is-open');
    document.documentElement.classList.add('cx-locked');
    document.querySelectorAll('[data-cx-open="' + name + '"][aria-expanded]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
    this.current = name;
    drawer.dispatchEvent(new CustomEvent('cx:drawer:open'));
    const focusTarget = drawer.querySelector('[autofocus], input[type="search"]') || drawer.querySelector('[data-cx-close]');
    setTimeout(() => focusTarget?.focus({ preventScroll: true }), 80);
    return true;
  },
  close(silent = false) {
    if (!this.current) return;
    const { drawer, overlay } = this.get(this.current);
    drawer?.classList.remove('is-open');
    overlay?.classList.remove('is-open');
    document.querySelectorAll('[data-cx-open="' + this.current + '"][aria-expanded]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    this.current = null;
    if (!silent) { document.documentElement.classList.remove('cx-locked'); CloseCursor.hide(); this.lastFocus?.focus?.({ preventScroll: true }); }
  },
};
document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-cx-open]');
  if (opener) { if (Drawers.open(opener.dataset.cxOpen, opener)) e.preventDefault(); return; }
  if (e.target.closest('[data-cx-close]') || e.target.matches('[data-cx-overlay]')) Drawers.close();
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') { if (Drawers.current) Drawers.close(); else Header.closeAll(true); }
  if (e.key === 'Tab' && Drawers.current) {
    const { drawer } = Drawers.get(Drawers.current);
    const items = [...drawer.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0]; const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
});

/* Cursor redondo de cierre sobre el fondo oscurecido */
const CloseCursor = {
  el: null,
  init() {
    this.el = document.querySelector('.cx-close-cursor');
    if (!this.el) return;
    document.addEventListener('pointermove', (e) => { if (e.pointerType !== 'mouse') return; const onOverlay = e.target instanceof Element && e.target.matches('.cx-overlay.is-open'); this.el.style.setProperty('--x', e.clientX + 'px'); this.el.style.setProperty('--y', e.clientY + 'px'); this.el.classList.toggle('is-visible', onOverlay); }, { passive: true });
    document.addEventListener('pointerleave', () => this.hide());
  },
  hide() { this.el?.classList.remove('is-visible'); },
};

/* Cabecera: desplegables y sombra al hacer scroll */
const Header = {
  root: null,
  closeAll(restoreFocus = false) {
    const root = this.root || document.querySelector('cx-header');
    if (!root) return;
    root.querySelectorAll('.cx-nav__item.is-open').forEach((item) => { item.classList.remove('is-open'); const btn = item.querySelector(':scope > .cx-nav__link'); btn?.setAttribute('aria-expanded', 'false'); if (restoreFocus && item.contains(document.activeElement)) btn?.focus(); });
    root.querySelector('[data-cx-drop-overlay]')?.classList.remove('is-open');
    if (!Drawers.current) CloseCursor.hide();
  },
  open(item) { this.closeAll(); item.classList.add('is-open'); item.querySelector(':scope > .cx-nav__link')?.setAttribute('aria-expanded', 'true'); this.root.querySelector('[data-cx-drop-overlay]')?.classList.add('is-open'); },
};
class CxHeader extends HTMLElement {
  connectedCallback() {
    Header.root = this;
    let timer;
    this.querySelectorAll('[data-cx-drop]').forEach((item) => {
      const btn = item.querySelector(':scope > .cx-nav__link');
      btn?.addEventListener('click', (e) => { e.preventDefault(); if (item.classList.contains('is-open')) Header.closeAll(); else Header.open(item); });
      item.addEventListener('pointerenter', (e) => { if (e.pointerType !== 'mouse') return; clearTimeout(timer); timer = setTimeout(() => Header.open(item), 90); });
      item.addEventListener('pointerleave', (e) => { if (e.pointerType !== 'mouse') return; clearTimeout(timer); timer = setTimeout(() => Header.closeAll(), 220); });
      item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) Header.closeAll(); });
    });
    this.querySelectorAll('.cx-nav__item:not([data-cx-drop])').forEach((item) => { item.addEventListener('pointerenter', (e) => { if (e.pointerType !== 'mouse') return; clearTimeout(timer); timer = setTimeout(() => Header.closeAll(), 90); }); });
    this.querySelector('[data-cx-drop-overlay]')?.addEventListener('click', () => Header.closeAll());
    document.addEventListener('click', (e) => { if (!this.contains(e.target)) Header.closeAll(); });
    const onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }
}

/* Carrito lateral */
class CxCart extends HTMLElement {
  connectedCallback() {
    this.cfg = getConfig();
    this.body = this.querySelector('[data-cx-cart-content]');
    this.empty = this.querySelector('[data-cx-cart-empty]');
    this.foot = this.querySelector('[data-cx-cart-foot]');
    this.total = this.querySelector('[data-cx-cart-total]');
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
    this.tabs.forEach((tab, i) => tab.addEventListener('click', () => this.selectTab(i)));
    this.addEventListener('cx:drawer:open', () => { this.selectTab(0); this.refresh(); });
    this.addEventListener('click', (e) => { const btn = e.target.closest('[data-cx-qty]'); if (!btn) return; const line = btn.closest('[data-key]'); this.change(line.dataset.key, Number(btn.dataset.cxQty), line); });
    document.addEventListener('shopify:cart:lines-update', (e) => {
      if (e.detail?.source === 'cx-cart') return;
      const after = () => { this.refresh().then(() => { if (e.action === 'add' || e.detail?.action === 'add') Drawers.open('cart'); }); };
      if (e.promise?.then) e.promise.then(after, () => {}); else setTimeout(after, 400);
    });
    document.addEventListener('cart:update', (e) => {
      if (e.detail?.data?.source === 'cx-cart') return;
      this.refresh().then(() => { const p = window.location.pathname; const onCartPage = p.endsWith('/cart') || p.endsWith('/cart/'); if (!onCartPage && !e.detail?.data?.didError) Drawers.open('cart'); });
    });
    document.addEventListener('cx:cart:changed', (e) => { if (e.detail?.handled) return; this.render(e.detail?.cart); if (e.detail?.open) Drawers.open('cart'); });
    if (Number(this.querySelector('[data-cx-cart-count]')?.textContent || 0) > 0) this.refresh();
  }
  selectTab(index) {
    this.tabs.forEach((t, i) => { t.setAttribute('aria-selected', String(i === index)); t.tabIndex = i === index ? 0 : -1; });
    this.panels.forEach((p, i) => { p.hidden = i !== index; });
    if (index === 1) this.renderRecent();
  }
  async refresh() {
    try { const cart = await fetch(this.cfg.routes.cart + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()); this.render(cart); return cart; } catch (e) { return null; }
  }
  async change(key, quantity, lineEl) {
    lineEl?.classList.add('is-busy');
    try {
      const res = await fetch(this.cfg.routes.cartChange + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
      const cart = await res.json();
      if (!res.ok || cart.status) throw new Error(cart.description || cart.message);
      this.render(cart);
    } catch (err) {
      lineEl?.classList.remove('is-busy');
      const msg = document.createElement('p');
      msg.className = 'cx-cart__error';
      msg.textContent = err?.message || this.cfg.strings.error || 'Error';
      lineEl?.querySelector('.cx-line__info')?.append(msg);
      setTimeout(() => msg.remove(), 4000);
    }
  }
  render(cart) {
    if (!cart) return;
    updateCartCounts(cart.item_count);
    const isEmpty = cart.item_count === 0;
    this.empty.hidden = !isEmpty;
    this.body.hidden = isEmpty;
    this.foot.hidden = isEmpty;
    if (this.total) this.total.textContent = formatMoney(cart.total_price);
    if (isEmpty) { this.body.innerHTML = ''; return; }
    const s = this.cfg.strings;
    let ship = '';
    const goal = Number(this.cfg.freeShipping || 0);
    if (goal > 0) {
      const left = Math.max(0, goal - cart.total_price);
      const pct = Math.min(100, Math.round((cart.total_price / goal) * 100));
      const text = left > 0 ? escapeHtml(s.shipLeft || '').replace('[amount]', '<strong>' + escapeHtml(formatMoney(left)) + '</strong>') : '<strong>' + escapeHtml(s.shipDone || '') + '</strong>';
      ship = '<div class="cx-ship' + (left === 0 ? ' is-done' : '') + '"><span>' + text + '</span><div class="cx-ship__bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + pct + '"><span style="--p:' + pct + '%"></span></div></div>';
    }
    const lines = cart.items.map((item) => {
      const vt = item.variant_title || '';
      const variant = vt && vt.toLowerCase() !== 'default title' ? '<span class="cx-line__variant">' + escapeHtml(vt) + '</span>' : '';
      const props = Object.entries(item.properties || {}).filter(([k, v]) => v && !k.startsWith('_')).map(([k, v]) => '<span class="cx-line__variant">' + escapeHtml(k) + ': ' + escapeHtml(v) + '</span>').join('');
      const was = item.original_line_price > item.final_line_price ? '<s>' + escapeHtml(formatMoney(item.original_line_price)) + '</s>' : '';
      const img = item.image ? '<img src="' + escapeHtml(sizedImage(item.image, 240)) + '" alt="" loading="lazy" width="96" height="96">' : '';
      return '<li class="cx-line" data-key="' + escapeHtml(item.key) + '">'
        + '<a class="cx-line__img" href="' + escapeHtml(item.url) + '" tabindex="-1" aria-hidden="true">' + img + '</a>'
        + '<div class="cx-line__info"><a class="cx-line__title" href="' + escapeHtml(item.url) + '">' + escapeHtml(item.product_title) + '</a>' + variant + props
        + '<div class="cx-line__row"><div class="cx-qty">'
        + '<button type="button" data-cx-qty="' + (item.quantity - 1) + '" aria-label="' + escapeHtml(s.decrease || '-') + '">−</button>'
        + '<span aria-live="polite">' + item.quantity + '</span>'
        + '<button type="button" data-cx-qty="' + (item.quantity + 1) + '" aria-label="' + escapeHtml(s.increase || '+') + '">+</button>'
        + '</div><button type="button" class="cx-line__remove" data-cx-qty="0">' + escapeHtml(s.remove || 'Eliminar') + '</button></div></div>'
        + '<div class="cx-line__price"><span>' + escapeHtml(formatMoney(item.final_line_price)) + '</span>' + was + '</div></li>';
    }).join('');
    this.body.innerHTML = ship + '<ul class="cx-lines" role="list">' + lines + '</ul>';
  }
  async renderRecent() {
    const box = this.querySelector('[data-cx-recent-body]');
    if (!box) return;
    const handles = Recent.list();
    if (!handles.length) { box.innerHTML = '<div class="cx-empty"><p>' + escapeHtml(this.cfg.strings.recentEmpty || '') + '</p></div>'; return; }
    const root = this.cfg.routes.root.endsWith('/') ? this.cfg.routes.root.slice(0, -1) : this.cfg.routes.root;
    const products = (await Promise.all(handles.map((h) => fetch(root + '/products/' + encodeURIComponent(h) + '.js').then((r) => (r.ok ? r.json() : null)).catch(() => null)))).filter(Boolean);
    box.innerHTML = '<ul class="cx-minis" role="list">' + products.map((p) => miniCard({ url: p.url, title: p.title, image: p.featured_image, price: p.price, compare: p.compare_at_price })).join('') + '</ul>';
  }
}
function miniCard({ url, title, image, price, compare, html }) {
  const sale = compare && compare > price;
  const img = image ? '<img src="' + escapeHtml(sizedImage(image, 200)) + '" alt="" loading="lazy" width="72" height="72">' : '';
  return '<li><a class="cx-mini" href="' + escapeHtml(url) + '"><span class="cx-mini__img">' + img + '</span>'
    + '<span><span class="cx-mini__title">' + (html || escapeHtml(title)) + '</span>'
    + '<span class="cx-mini__price"><span' + (sale ? ' class="is-sale"' : '') + '>' + escapeHtml(formatMoney(price)) + '</span>' + (sale ? '<s>' + escapeHtml(formatMoney(compare)) + '</s>' : '') + '</span></span>'
    + '<span class="cx-mini__go">' + ICON_ARROW + '</span></a></li>';
}

/* Vistos recientemente */
const Recent = {
  key: 'cx-recent',
  list() { try { return JSON.parse(localStorage.getItem(this.key) || '[]').slice(0, 8); } catch (e) { return []; } },
  add(handle) { if (!handle) return; try { const list = this.list().filter((h) => h !== handle); list.unshift(handle); localStorage.setItem(this.key, JSON.stringify(list.slice(0, 8))); } catch (e) { /* sin almacenamiento */ } },
};
if (window.cxProductHandle) Recent.add(window.cxProductHandle);

/* Buscador lateral con resultados al escribir */
function highlight(text, q) {
  const words = q.toLowerCase().split(' ').filter(Boolean);
  const lower = String(text).toLowerCase();
  let out = ''; let i = 0;
  while (i < text.length) {
    let hit = null;
    for (const w of words) { if (lower.startsWith(w, i)) { hit = w; break; } }
    if (hit) { out += '<mark>' + escapeHtml(text.slice(i, i + hit.length)) + '</mark>'; i += hit.length; }
    else { out += escapeHtml(text[i]); i += 1; }
  }
  return out;
}
class CxSearch extends HTMLElement {
  connectedCallback() {
    this.cfg = getConfig();
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-cx-search-results]');
    this.idle = this.querySelector('[data-cx-search-idle]');
    this.clear = this.querySelector('[data-cx-search-clear]');
    let timer;
    this.input?.addEventListener('input', () => { clearTimeout(timer); this.clear.hidden = !this.input.value; timer = setTimeout(() => this.search(this.input.value.trim()), 220); });
    this.clear?.addEventListener('click', () => { this.input.value = ''; this.clear.hidden = true; this.search(''); this.input.focus(); });
    this.querySelectorAll('[data-cx-term]').forEach((chip) => chip.addEventListener('click', () => { this.input.value = chip.dataset.cxTerm; this.clear.hidden = false; this.search(this.input.value); }));
  }
  async search(q) {
    this.abort?.abort();
    if (!q) { this.results.innerHTML = ''; this.idle.hidden = false; return; }
    this.idle.hidden = true;
    this.results.innerHTML = '<p class="cx-search__status">' + escapeHtml(this.cfg.strings.searching || '…') + '</p>';
    this.abort = new AbortController();
    const params = new URLSearchParams({ q, 'resources[type]': 'product,collection,query', 'resources[limit]': '6', 'resources[options][unavailable_products]': 'last' });
    try {
      const data = await fetch(this.cfg.routes.predictive + '.json?' + params.toString(), { signal: this.abort.signal, headers: { Accept: 'application/json' } }).then((r) => r.json());
      this.render(q, data?.resources?.results || {});
    } catch (err) { if (err.name !== 'AbortError') this.results.innerHTML = ''; }
  }
  render(q, r) {
    const s = this.cfg.strings;
    const products = r.products || [];
    const collections = r.collections || [];
    const queries = (r.queries || []).filter((x) => x.text.toLowerCase() !== q.toLowerCase());
    if (!products.length && !collections.length && !queries.length) { this.results.innerHTML = '<p class="cx-search__status">' + escapeHtml((s.noResults || '').replace('[terms]', q)) + '</p>'; return; }
    const toCents = (v) => Math.round(parseFloat(v || 0) * 100);
    let html = '';
    if (queries.length) html += '<div class="cx-search__group"><p class="cx-search__label">' + escapeHtml(s.suggestions || '') + '</p><div class="cx-search__chips">' + queries.slice(0, 5).map((x) => '<a class="cx-search__chip" href="' + escapeHtml(x.url) + '">' + escapeHtml(x.text) + '</a>').join('') + '</div></div>';
    if (products.length) html += '<div class="cx-search__group"><p class="cx-search__label">' + escapeHtml(s.products || '') + '</p><ul class="cx-minis" role="list">' + products.map((p) => miniCard({ url: p.url, image: p.featured_image?.url || p.image, price: toCents(p.price), compare: toCents(p.compare_at_price_max), html: highlight(p.title, q) })).join('') + '</ul></div>';
    if (collections.length) html += '<div class="cx-search__group"><p class="cx-search__label">' + escapeHtml(s.collections || '') + '</p><div class="cx-search__chips">' + collections.map((c) => '<a class="cx-search__chip" href="' + escapeHtml(c.url) + '">' + escapeHtml(c.title) + '</a>').join('') + '</div></div>';
    html += '<div class="cx-search__group"><a class="cx-btn cx-btn--block" href="' + escapeHtml(this.cfg.routes.search) + '?type=product&q=' + encodeURIComponent(q) + '">' + escapeHtml(s.viewAll || '') + ' ' + ICON_ARROW + '</a></div>';
    this.results.innerHTML = html;
  }
}

/* Menú inferior móvil: se oculta al bajar */
function initDock() {
  const dock = document.querySelector('.cx-dock');
  if (!dock) return;
  let last = window.scrollY;
  window.addEventListener('scroll', () => { const y = window.scrollY; if (Math.abs(y - last) < 8) return; dock.classList.toggle('is-hidden', y > last && y > 300); last = y; }, { passive: true });
  dock.querySelector('[data-cx-open-search]')?.addEventListener('click', (e) => { if (Drawers.open('search', e.currentTarget)) return; const trigger = document.querySelector('search-button button'); if (trigger) trigger.click(); else window.location.href = dock.dataset.searchUrl || '/search'; });
}

/* Registro */
const defs = { 'cx-slideshow': CxSlideshow, 'cx-carousel': CxCarousel, 'cx-hotspots': CxHotspots, 'cx-tabs': CxTabs, 'cx-header': CxHeader, 'cx-cart': CxCart, 'cx-search': CxSearch };
for (const [name, cls] of Object.entries(defs)) { if (!customElements.get(name)) customElements.define(name, cls); }
observeReveals();
initParallax();
CloseCursor.init();
initDock();
document.addEventListener('shopify:section:load', (e) => { observeReveals(e.target); initParallax(); });
